import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router";
import { DashboardLayout } from "@/components/layout/dashboard-layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Plus,
  Trash2,
  Save,
  Download,
  RefreshCw,
  Home,
  CheckCircle
} from "lucide-react";
import { generatePurchaseBill } from "@/utils/pdf-generator";
import { PurchaseItem, SelectData, Purchase } from "@/types/api";
import { purchaseService, vendorService, categoryService, productService } from "@/services/api";
import { useToast } from "@/hooks/use-toast";

const PurchasePage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [vendor, setVendor] = useState("");
  const [vendors, setVendors] = useState<SelectData[]>([]);
  const [categories, setCategories] = useState<SelectData[]>([]);
  const [purchaseDate, setPurchaseDate] = useState(new Date().toISOString().split('T')[0]);
  const [items, setItems] = useState<PurchaseItem[]>([]);
  const [grandTotal, setGrandTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProductsForItem, setLoadingProductsForItem] = useState<string | null>(null);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [billNumber, setBillNumberr] = useState("");
  const [lastPurchaseData, setLastPurchaseData] = useState<{
    vendor: string;
    purchaseDate: string;
    items: PurchaseItem[];
    grandTotal: number;
  } | null>(null);
  const [productsByCategory, setProductsByCategory] = useState<{ [key: number]: SelectData[] }>({});

  // Generate a new empty row
  const generateNewItem = (): PurchaseItem => ({
    id: Date.now().toString(),
    categoryId: 0,
    categoryName: "",
    productId: 0,
    productName: "",
    unitPrice: 0,
    tax: 0,
    quantity: 1,
    discount: 0,
    total: 0,
    billNumber: ""
  });

  // Fetch vendors and categories on component mount
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [vendorsData, categoriesData] = await Promise.all([
          await vendorService.getForSelect(),
          await categoryService.getForSelect()
        ]);
        setVendors(vendorsData);
        setCategories(categoriesData);
      } catch (error) {
        toast({
          variant: "destructive",
          title: "Fetch Categories",
          description: error instanceof Error ? error.message : error
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Add a new empty row
  const handleAddItem = () => {
    setItems([...items, generateNewItem()]);
  };

  // Clear all items
  const handleClearItems = () => {
    setItems([]);
    setGrandTotal(0);
  };

  // Handle category selection
  const handleCategoryChange = async (itemId: string, categoryId: number) => {
    setLoadingProductsForItem(itemId);
    try {
      if (items.find(i => i.categoryId === categoryId)) {
        toast({
          variant: "destructive",
          title: "Purchase Entry",
          description: "Category already selected"
        });
        return; // If the category is already selected, do nothing])
      }

      const selectedCategory = categories.find(c => c.id === categoryId);
      let fetchedProducts: SelectData[] = [];
      if (productsByCategory[categoryId]) // Check if the products for this category are already cached
        fetchedProducts = productsByCategory[categoryId]; // Load products from cache
      else {
        fetchedProducts = await productService.getForSelect(categoryId);// Call API to fetch products for the selected category
        // Update the cache
        setProductsByCategory((prev) => ({
          ...prev,
          [categoryId]: fetchedProducts,
        }));

        // Update the item with the selected category
        setItems(items.map(item => {
          if (item.id === itemId) {
            return {
              ...item,
              categoryId,
              categoryName: selectedCategory.name,
              productId: 0, // Reset product when category changes
              productName: "",
              unitPrice: 0,
              tax: 0,
              quantity: 1,
              total: 0
            };
          }
          return item;
        }));
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Fetch Products",
        description: error instanceof Error ? error.message : error
      });
    } finally {
      setLoadingProductsForItem(null);
    }
  };

  // Handle product selection
  const handleProductChange = async (itemId: string, productId: number) => {
    try {
      const item = items.find(i => i.id === itemId);
      if (!item) return;

      if (productsByCategory[item.categoryId]) {
        // Find the product with the matching productId
        const product = productsByCategory[item.categoryId].find((p) => p.id === productId);
        setItems(items.map(item => {
          if (item.id === itemId) {
            const updatedItem = {
              ...item,
              productId,
              productName: product.name
            };
            return updatedItem;
          }
          return item;
        }));

        return product ? product.name : null; // Return the name if found, otherwise null
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Get Product Details",
        description: error instanceof Error ? error.message : error
      });
    }
  };

  // Calculate total for a single item
  const calculateItemTotal = (quantity: number, unitPrice: number, tax: number, discount: number) => {
    const subtotal = quantity * unitPrice;
    const taxAmount = subtotal * (tax / 100);
    const discountAmount = subtotal * (discount / 100);
    return subtotal + taxAmount - discountAmount;
  };

  // Handle quantity change
  const handleQuantityChange = (itemId: string, quantity: number) => {
    setItems(items.map(item => {
      if (item.id === itemId) {
        const validQuantity = Math.max(1, quantity);
        const updatedItem = {
          ...item,
          quantity: validQuantity,
          total: calculateItemTotal(validQuantity, item.unitPrice, item.tax, item.discount)
        };
        return updatedItem;
      }
      return item;
    }));
  };

  // Handle unitPrice change
  const handleUnitPriceChange = (itemId: string, unitPrice: number) => {
    setItems(items.map(item => {
      if (item.id === itemId) {
        const validUnitPrice = Math.max(0, unitPrice);
        const updatedItem = {
          ...item,
          unitPrice: validUnitPrice,
          total: calculateItemTotal(item.quantity, validUnitPrice, item.tax, item.discount)
        };
        return updatedItem;
      }
      return item;
    }));
  };

  // Handle tax change
  const handleTaxChange = (itemId: string, tax: number) => {
    setItems(items.map(item => {
      if (item.id === itemId) {
        const validTax = Math.min(Math.max(0, tax), 100);
        const updatedItem = {
          ...item,
          tax: validTax,
          total: calculateItemTotal(item.quantity, item.unitPrice, validTax, item.discount)
        };
        return updatedItem;
      }
      return item;
    }));
  };

  // Handle discount change
  const handleDiscountChange = (itemId: string, discount: number) => {
    setItems(items.map(item => {
      if (item.id === itemId) {
        const validDiscount = Math.min(Math.max(0, discount), 100);
        const updatedItem = {
          ...item,
          discount: validDiscount,
          total: calculateItemTotal(item.quantity, item.unitPrice, item.tax, validDiscount)
        };
        return updatedItem;
      }
      return item;
    }));
  };

  // Remove an item
  const handleRemoveItem = (itemId: string) => {
    setItems(items.filter(item => item.id !== itemId));
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!vendor) {
      toast({
        variant: "destructive",
        title: "Save Purchase",
        description: "Please select a vendor"
      });
      return;
    }
    if (items.length === 0) {
      toast({
        variant: "destructive",
        title: "Save Purchase",
        description: "Please add at least one item"
      });
      return;
    }
    if (items.some(item => !item.categoryId || !item.productId)) {
      toast({
        variant: "destructive",
        title: "Save Purchase",
        description: "Please select a category and product for all items"
      });
      return;
    }

    setIsLoading(true);

    const selectedVendor = vendors.find(v => v.id === Number(vendor));

    const newPurchase: Purchase = {
      vendorId: selectedVendor.id,
      purchasedAt: purchaseDate,
      type: 1,
      paymentMode: 1,
      items: items,
      total: grandTotal,
      billNumber: billNumber
    };
    const result = await purchaseService.createItem(newPurchase);
    if (result > 0) {
      setLastPurchaseData({
        vendor: selectedVendor.name,
        purchaseDate,
        items,
        grandTotal
      });
      setIsLoading(false);
      setSuccessDialogOpen(true);
    }
    else {
      setIsLoading(false);
      toast({
        variant: "destructive",
        title: "Save Purchase",
        description: "Failed to save purchase"
      });
    }
  };

  // Handle download bill
  const handleDownloadBill = () => {
    if (lastPurchaseData) {
      generatePurchaseBill(lastPurchaseData);
    }
  };

  // Handle new purchase
  const handleNewPurchase = () => {
    // Reset the form
    setVendor("");
    setPurchaseDate(new Date().toISOString().split('T')[0]);
    setItems([]);
    setGrandTotal(0);
    setSuccessDialogOpen(false);
  };

  // Handle close and redirect to dashboard
  const handleClose = () => {
    setSuccessDialogOpen(false);
    navigate("/dashboard");
  };

  // Update grand total whenever items change
  useEffect(() => {
    const total = items.reduce((sum, item) => sum + item.total, 0);
    setGrandTotal(total);
  }, [items]);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold">New Purchase</h1>
          {/* <Button variant="outline" size="sm">
            <FileText size={16} className="mr-2" />
            View Purchases
          </Button> */}
        </div>

        {/* Section 1: Header with vendor name and date */}
        <Card>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label htmlFor="vendor">Vendor</Label>
                <Select value={vendor} onValueChange={setVendor}>
                  <SelectTrigger id="vendor">
                    <SelectValue placeholder="Select a vendor" />
                  </SelectTrigger>
                  <SelectContent>
                    {vendors.map(v => (
                      <SelectItem key={v.id} value={v.id.toString()}>{v.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="date">Bill Number</Label>
                <Input
                  id="billNumber"
                  value={billNumber}
                  onChange={(e) => setBillNumberr(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="date">Purchase Date</Label>
                <Input
                  id="date"
                  type="date"
                  value={purchaseDate}
                  onChange={(e) => setPurchaseDate(e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 2: Add, Clear buttons */}
        <div className="flex justify-end gap-2">
          <Button onClick={handleAddItem}>
            <Plus size={16} className="mr-2" />
            Add Item
          </Button>
          <Button variant="outline" onClick={handleClearItems}>
            <Trash2 size={16} className="mr-2" />
            Clear All
          </Button>
        </div>

        {/* Section 3: Editable table */}
        <div className="border rounded-md">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Category</TableHead>
                <TableHead>Product</TableHead>
                <TableHead>Quantity</TableHead>
                <TableHead>Unit Price</TableHead>
                <TableHead>Tax (%)</TableHead>
                <TableHead>Discount (%)</TableHead>
                <TableHead>Total</TableHead>
                <TableHead className="w-[80px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-6 text-muted-foreground">
                    No items added. Click "Add Item" to start.
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <Select
                        value={item.categoryId.toString()}
                        onValueChange={(value) => handleCategoryChange(item.id, Number(value))}
                      >
                        <SelectTrigger className="w-[180px]">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map(category => (
                            <SelectItem key={category.id} value={category.id.toString()}>
                              {category.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={item.productId.toString()}
                        onValueChange={(value) => handleProductChange(item.id, Number(value))}
                        disabled={!item.categoryId || loadingProductsForItem === item.id}
                      >
                        <SelectTrigger className="w-[180px]">
                          <SelectValue placeholder={
                            !item.categoryId
                              ? "Select category first"
                              : loadingProductsForItem === item.id
                                ? "Loading products..."
                                : "Select product"
                          } />
                        </SelectTrigger>
                        <SelectContent>
                          {item.categoryId && productsByCategory[item.categoryId].map(product => (
                            <SelectItem key={product.id} value={product.id.toString()}>
                              {product.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => handleQuantityChange(item.id, parseInt(e.target.value) || 1)}
                        className="w-20"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.unitPrice}
                        onChange={(e) => handleUnitPriceChange(item.id, parseFloat(e.target.value) || 0)}
                        className="w-24"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={item.tax}
                        onChange={(e) => handleTaxChange(item.id, parseFloat(e.target.value) || 0)}
                        className="w-20"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={item.discount}
                        onChange={(e) => handleDiscountChange(item.id, parseFloat(e.target.value) || 0)}
                        className="w-20"
                      />
                    </TableCell>
                    <TableCell className="font-medium">
                      {item.total.toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveItem(item.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Section 4: Submit button and grand total */}
        <div className="flex justify-between items-center">
          <Button
            onClick={handleSubmit}
            disabled={
              items.length === 0 ||
              !vendor ||
              grandTotal === 0 ||
              isLoading
            }
          >
            {isLoading ? (
              <>
                <RefreshCw size={16} className="mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Save size={16} className="mr-2" />
                Submit Purchase
              </>
            )}
          </Button>
          <div className="text-right">
            <p className="text-sm text-muted-foreground">Grand Total</p>
            <p className="text-2xl font-bold">{grandTotal.toFixed(2)}</p>
          </div>
        </div>

        {/* Success Dialog */}
        <Dialog open={successDialogOpen} onOpenChange={setSuccessDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-green-600">
                <CheckCircle className="h-6 w-6" />
                Purchase Completed Successfully
              </DialogTitle>
              <DialogDescription>
                Your purchase has been processed successfully. What would you like to do next?
              </DialogDescription>
            </DialogHeader>

            <div className="py-4">
              <Alert className="bg-green-50 border-green-200 text-green-800">
                <CheckCircle className="h-4 w-4" />
                <AlertDescription>
                  Purchase of Rs {grandTotal.toFixed(2)} from {vendors.find(v => v.name === vendor)?.name} has been recorded.
                </AlertDescription>
              </Alert>
            </div>

            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={handleDownloadBill}
              >
                <Download size={16} className="mr-2" />
                Download Bill
              </Button>
              <Button
                variant="outline"
                className="flex-1"
                onClick={handleNewPurchase}
              >
                <Plus size={16} className="mr-2" />
                New Purchase
              </Button>
              <Button
                variant="default"
                className="flex-1"
                onClick={handleClose}
              >
                <Home size={16} className="mr-2" />
                Dashboard
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default PurchasePage;
